import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/lib/i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Export / delete data. Both use EXISTING endpoints:
 *  - Export → `org.export` (the caller's own data; downloaded as JSON).
 *  - Delete → `auth.deleteAccount({confirm:"DELETE"})` — destructive, so the
 *    button stays disabled until the user types DELETE exactly. The server
 *    re-validates the literal (`z.literal("DELETE")`), so the client gate is UX,
 *    not the security boundary. The confirm word stays the literal "DELETE"
 *    in both locales — it's the server contract, not copy.
 */
export function AccountDataSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const [confirmText, setConfirmText] = useState("");

  const exportMut = trpc.org.export.useMutation({
    onSuccess: data => {
      try {
        const blob = new Blob([JSON.stringify(data, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "receipt-export.json";
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        /* download is best-effort; the data was returned successfully */
      }
      toast.success(isAr ? "ملف بياناتك جاهز." : "Your data export is ready.");
    },
    onError: () =>
      toast.error(
        isAr
          ? "تعذّر تصدير بياناتك. حاول مرة أخرى."
          : "Couldn’t export your data. Please try again.",
      ),
  });

  const deleteMut = trpc.auth.deleteAccount.useMutation({
    onSuccess: () => {
      toast.success(isAr ? "تم حذف الحساب." : "Account deleted.");
      window.location.href = "/";
    },
    onError: () =>
      toast.error(
        isAr
          ? "تعذّر حذف حسابك. حاول مرة أخرى."
          : "Couldn’t delete your account. Please try again.",
      ),
  });

  const canDelete = confirmText === "DELETE" && !deleteMut.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="account-data-sheet">
        <DialogHeader>
          <DialogTitle>
            {isAr ? "تصدير بياناتك أو حذفها" : "Export or delete your data"}
          </DialogTitle>
          <DialogDescription>
            {isAr
              ? "نزّل كل ما نحتفظ به عنك، أو احذف حسابك نهائياً."
              : "Download everything we hold about you, or permanently delete your account."}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Download className="h-4 w-4 text-coral" />{" "}
            {isAr ? "تصدير بياناتي" : "Export my data"}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {isAr
              ? "ملف JSON يضم ملخّصاتك وإشاراتك وتفضيلاتك والمزيد."
              : "A JSON file with your receipts, signals, preferences and more."}
          </p>
          <Button
            onClick={() => exportMut.mutate()}
            disabled={exportMut.isPending}
            variant="outline"
            size="sm"
            className="mt-3 rounded-full"
            data-testid="export-button"
          >
            {exportMut.isPending ? "…" : isAr ? "تنزيل البيانات" : "Download export"}
          </Button>
        </div>

        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-destructive">
            <AlertTriangle className="h-4 w-4" />{" "}
            {isAr ? "حذف حسابي" : "Delete my account"}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {isAr ? (
              <>
                يحذف هذا حسابك وكل بياناتك نهائياً، ولا يمكن التراجع عنه. اكتب{" "}
                <span className="font-mono font-semibold">DELETE</span> للتأكيد.
              </>
            ) : (
              <>
                This permanently removes your account and all your data. This cannot
                be undone. Type{" "}
                <span className="font-mono font-semibold">DELETE</span> to confirm.
              </>
            )}
          </p>
          <input
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder="DELETE"
            data-testid="delete-confirm-input"
            className="mt-3 w-full rounded-lg border border-border bg-background p-2 text-sm outline-none focus:border-destructive"
          />
          <Button
            onClick={() => deleteMut.mutate({ confirm: "DELETE" })}
            disabled={!canDelete}
            size="sm"
            className="mt-3 rounded-full bg-destructive text-white hover:bg-destructive/90"
            data-testid="delete-button"
          >
            <Trash2 className="h-3.5 w-3.5 me-1" />
            {deleteMut.isPending ? "…" : isAr ? "حذف الحساب" : "Delete account"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
